import { useState, type FormEvent } from "react";

import { refusalText } from "../../../api/errors";
import { useApiQuery } from "../../../api/hooks";
import type { ProjectListView } from "../../../api/types";
import { useCurrentMe } from "../../../app/auth";
import { Button, Card, ConfirmDialog, EmptyState, Loading } from "../../../ui";
import { FormError, TextInput } from "../components/fields";
import { useApiUpload, useCommandId, useRefresh } from "../hooks";
import { plural, whenWords } from "../lib/text";

const SHOWN = 20;

/** The screen at /project-lists: the codes each project defines for itself (piping class, insulation class, and so on). */
export function ProjectListsPage() {
  const me = useCurrentMe();
  const editable = me.active?.roles.includes("engineer") ?? false;
  const lists = useApiQuery("project_lists", {});

  return (
    <div className="stack item-screen">
      <Card aria-labelledby="lists-title">
        <h1 id="lists-title">Project code lists</h1>
        <p>
          A field of the kind &quot;code from a project list&quot; names the kind of list its codes come from. Each project loads its own codes here.
          Loading a list of a kind that is already loaded replaces it.
        </p>
      </Card>

      {lists.isPending ? (
        <Loading>Reading the lists</Loading>
      ) : lists.isError ? (
        <Card>
          <EmptyState title="The lists could not be read">{refusalText(lists.error)}</EmptyState>
        </Card>
      ) : lists.data.length === 0 ? (
        <Card>
          <EmptyState title="No project list is loaded yet">Fields that read a project list show &quot;list not loaded yet&quot; until you load the codes here.</EmptyState>
        </Card>
      ) : (
        lists.data.map((list) => <ListCard key={list.id} list={list} />)
      )}

      {editable && <LoadCard existingKinds={(lists.data ?? []).map((l) => l.kind)} />}
    </div>
  );
}

function ListCard({ list }: { list: ProjectListView }) {
  const [filter, setFilter] = useState("");
  const [all, setAll] = useState(false);
  const wanted = filter.trim().toLowerCase();
  const matches = wanted ? list.codes.filter((c) => c.code.toLowerCase().includes(wanted) || c.description.toLowerCase().includes(wanted)) : list.codes;
  const shown = all ? matches : matches.slice(0, SHOWN);
  return (
    <Card aria-label={list.kind}>
      <h2>{list.kind}</h2>
      <p className="muted">
        {plural(list.count, "code")}. Loaded by {list.loaded_by.display}, {whenWords(list.at)}.
      </p>
      {list.codes.length > SHOWN && <TextInput label="Find a code" type="search" value={filter} onChange={setFilter} />}
      {shown.length === 0 ? (
        <p className="muted">No code matches.</p>
      ) : (
        <table className="code-table">
          <thead>
            <tr>
              <th scope="col">Code</th>
              <th scope="col">Description</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((c) => (
              <tr key={c.code}>
                <td>
                  <code>{c.code}</code>
                </td>
                <td>{c.description}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {matches.length > shown.length && (
        <Button variant="quiet" onClick={() => setAll(true)}>
          Show all {matches.length} codes
        </Button>
      )}
    </Card>
  );
}

function LoadCard({ existingKinds }: { existingKinds: string[] }) {
  const [kind, setKind] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [replacing, setReplacing] = useState(false);
  const load = useApiUpload("load_project_list");
  const ids = useCommandId();
  const refresh = useRefresh();
  const replaces = existingKinds.some((k) => k.toLowerCase() === kind.trim().toLowerCase());

  const send = () => {
    if (!file) return;
    load.mutate(
      { file, input: { command_id: ids.current(), kind: kind.trim() } },
      {
        onSuccess: () => {
          ids.renew();
          setReplacing(false);
          setFile(null);
          setKind("");
          refresh("project_lists", "item_page");
        },
      },
    );
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const found: Record<string, string> = {};
    if (!kind.trim()) found.kind = "Name the kind of list.";
    if (!file) found.file = "Choose the file of codes and descriptions.";
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    if (replaces) setReplacing(true);
    else send();
  };

  return (
    <Card aria-labelledby="load-title">
      <h2 id="load-title">Load a list</h2>
      <form onSubmit={submit} noValidate className="stack" aria-label="Load a project list">
        <TextInput
          label="Kind of list"
          required
          hint="The name the fields use for it. A field that names this kind reads this list."
          value={kind}
          error={errors.kind}
          list="known-kinds"
          onChange={setKind}
        />
        <datalist id="known-kinds">
          {existingKinds.map((k) => (
            <option key={k} value={k} />
          ))}
        </datalist>
        <div className="field">
          <label htmlFor="list-file" className="field__label">
            The file <span className="field__required">(needed)</span>
          </label>
          <p className="field__hint muted">A CSV or an Excel file with a code and a description on each line.</p>
          <input id="list-file" type="file" className="field__input" accept=".csv,.xlsx" aria-invalid={errors.file ? true : undefined} onChange={(event) => setFile(event.target.files?.[0] ?? null)} />
          {errors.file && (
            <p role="alert" className="field__error">
              {errors.file}
            </p>
          )}
        </div>
        <div className="row">
          <Button type="submit" variant="primary" busy={load.isPending}>
            Load the list
          </Button>
        </div>
        <FormError message={load.error ? refusalText(load.error) : null} />
      </form>
      <ConfirmDialog
        open={replacing}
        title="Replace the list?"
        message={`A list of the kind "${kind.trim()}" is loaded already. The new file replaces it.`}
        confirmLabel="Replace it"
        busy={load.isPending}
        error={load.error ? refusalText(load.error) : null}
        onCancel={() => setReplacing(false)}
        onConfirm={send}
      />
    </Card>
  );
}
